import { Injectable } from '@angular/core';
import { SeasonService } from '../season.service';
import { RacesService } from '../races/races.service';
import {
  filter,
  first,
  map,
  mergeMap,
  pairwise,
  switchMap,
  tap,
  toArray
} from 'rxjs/operators';
import { SyncedStorage } from '../../app-commons/synced-storage/synced-storage';
import { concat, from, of, Subscription } from 'rxjs';
import { RemoteCommand } from './remote-command';
import { Season } from '../season';
import { RacePlan } from '../race-plan/race-plan';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private static readonly CLEAR_CACHE_COMMAND_ID = 'RemoteCommand#clearCache';
  private static readonly clientId = SyncedStorage.generateTypedDocId('ClientId');

  remoteClearCacheCommandExecutor$ = this.seasonService.syncedStorage$.pipe(
    switchMap((syncedStorage) => syncedStorage.watchId$<RemoteCommand>('RemoteCommand', 'clearCache')),
    pairwise(),
    map(([a, b]) => b),
    filter((command) => !!command && command.senderClientId !== SettingsService.clientId),
    map((command) => this.clearCache())
  );

  constructor(
    private readonly seasonService: SeasonService,
    private readonly racesService: RacesService
  ) {
  }

  detectSeasonSyncedDbName() {
    let dbName = SettingsService.hostToDbName();
    if (dbName) {
      this.changeSeasonSyncedDb(dbName);
    }
  }

  changeSeasonSyncedDb(dbName: string) {
    this.seasonService.syncedStorage$.next(
      new SyncedStorage<Season | RacePlan | RemoteCommand>(dbName, !!environment.isAdmin)
    );
  }

  async listAllDbs(): Promise<string[]> {
    let indexedDB = window.indexedDB;
    if ('databases' in indexedDB) {
      return (indexedDB as any).databases().then(databases => {
        return databases
          .map(db => db.name)
          .filter(dbName => dbName.includes('_pouch_'));
      });
    } else {
      return of(['no indexedDB engine found']).toPromise();
    }
  }

  compactRemoteDatabase(): Subscription {
    return this.seasonService.syncedStorage$.pipe(
      switchMap((syncedStorage) => syncedStorage.createRemoteDbCompactor$())
    ).subscribe(() => {
      console.log('Remote database compaction succeeded.');
    });
  }

  destroyAllSeasonDatabase(): Subscription {
    let syncStorage = new SyncedStorage(null, false);

    return concat(
      this.racesService.racePlans$$.pipe(
        first(),
        switchMap((raceWatchers) => raceWatchers),
        mergeMap(racePlan$ => racePlan$.pipe(first())),
        map((racePlan) => racePlan.resultsCouchDbName),
        filter((dbName) => !!dbName),
        mergeMap((dbName) => syncStorage.changeDb(dbName).pipe(first())),
        map((db) => {
          return syncStorage;
        })
      ),
      this.seasonService.syncedStorage$.pipe(first())
    ).pipe(
      mergeMap((syncedStorage) => {
        return syncedStorage.createLocalDbDeleter$();
      }),
      toArray()
    ).subscribe((deleted: string[]) => {
      console.warn('Deleted databases:\n\n' + deleted.join('\n'));
      alert('The app needs to reload.');
      location.reload();
    });
  }

  destroyAllLocalDatabases(): Subscription {
    return from(this.listAllDbs()).pipe(
      switchMap((dbNames => {
        return this.seasonService.syncedStorage$.pipe(
          switchMap((syncedStorage) => syncedStorage.createSyncTerminator$()),
          tap((sync) => {
            dbNames.forEach(dbName => window.indexedDB.deleteDatabase(dbName));
            console.warn('Deleted databases:\n\n' + dbNames.join('\n'));
            alert('The app need to reload.');
            location.reload();
          }),
        )
      }))
    ).subscribe();
  }

  clearCache(): Subscription {
    console.warn('Executing clearCache.');
    return this.destroyAllSeasonDatabase();
  }

  sendClearCacheCommand(): Subscription {
    return this.seasonService.syncedStorage$.pipe(
      switchMap((syncedStorage) => {
        return syncedStorage.save$<RemoteCommand>({
          _id: SettingsService.CLEAR_CACHE_COMMAND_ID,
          command: 'clearCache',
          time: (new Date()).toISOString(),
          senderClientId: SettingsService.clientId
        });
      })
    ).subscribe((response) => {
      console.log('Clear cache command sent.');
    });
  }

  private static hostToDbName() {
    if (window.location.host in environment.hostToSeasonSyncDbNameMap) {
      return environment.hostToSeasonSyncDbNameMap[window.location.host];
    }
  }
}
